import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';

import Selection, { getBoundsForNode } from './Selection';
import dates from './utils/dates';
import { isSelected } from './utils/selection';
import localizer from './localizer';

import { notify } from './utils/helpers';
import { accessor } from './utils/propTypes';
import { accessor as get } from './utils/accessors';

import TimeColumn from './TimeColumn';
import Event from './Event';

function snapToSlot(date, step) {
  const roundTo = 1000 * 60 * step;
  return new Date(Math.floor(date.getTime() / roundTo) * roundTo);
}

function wallClockMinutes(date) {
  return (date.getHours() * 60) + date.getMinutes() + (date.getSeconds() / 60);
}

function positionFromDate(date, min, step) {
  if (!date || !min) return 0;
  const minClock = wallClockMinutes(min);
  const dateClock = wallClockMinutes(date);
  return Math.max(0, Math.round(dateClock - minClock));
}

function minToDate(min, date) {
  let dt = new Date(date);
  const totalMins = wallClockMinutes(date);

  dt = dates.hours(dt, 0);
  dt = dates.minutes(dt, totalMins + min);
  dt = dates.seconds(dt, 0);
  return dates.milliseconds(dt, 0);
}

function overlaps(event, events, { startAccessor, endAccessor }, last) {
  const eStart = get(event, startAccessor);

  let offset = last;

  function overlap(eventB) {
    return dates.lt(eStart, get(eventB, endAccessor));
  }

  if (!events.length) return last - 1;
  events.reverse().some((prevEvent) => {
    if (overlap(prevEvent)) return true;
    offset -= 1;
    return null;
  });

  return offset;
}

export const DaySlot = ({
  events,
  step,
  min,
  max,
  allDayAccessor,
  startAccessor,
  endAccessor,
  selectable,
  eventOffset,
  onSelecting,
  onSelectSlot,
  className,
  rtl,
  now,
  selectRangeFormat,
  culture,
  timeslots,
  selected,
  titleAccessor,
  eventTimeRangeFormat,
  eventPropGetter,
  onSelectEvent,
  eventComponent,
}) => {
  const handleEventClick = (event) => {
    if (onSelectEvent) {
      onSelectEvent(event);
    }
  };

  const [selecting, setSelecting] = useState(false);
  const [startSlot, setStartSlot] = useState(null);
  const [endSlot, setEndSlot] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const selectorRef = useRef(null);
  const clickTimerRef = useRef(null);
  const initialDateSlotRef = useRef(null);
  const totalMinRef = useRef(0);
  const nodeRef = useRef(null);

  // Keep visual day-column math based on wall-clock time (24h),
  // so DST fallback/spring-forward doesn't shift event rows.
  totalMinRef.current = 24 * 60;

  const _select = useCallback((event) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    notify(onSelectEvent, event);
  }, [onSelectEvent]);

  const _slotStyle = useCallback((startSlot, _endSlot, leftOffset) => {
    const endSlot = Math.max(_endSlot, startSlot + step); // must be at least one `step` high

    const eventOffsetValue = eventOffset || 10;
    const isRtl = rtl;

    const top = ((startSlot / totalMinRef.current) * 100);
    const bottom = ((endSlot / totalMinRef.current) * 100);
    const per = leftOffset === 0 ? 0 : leftOffset * eventOffsetValue;

    return {
      top: `${top}%`,
      height: `${bottom - top}%`,
      [isRtl ? 'right' : 'left']: `${per}%`,
    };
  }, [step, eventOffset, rtl]);

  const _selectSlot = useCallback(({ startDate, endDate }) => {
    let current = startDate;
    const slots = [];

    while (dates.lte(current, endDate)) {
      slots.push(current);
      current = dates.add(current, step, 'minutes');
    }

    notify(onSelectSlot, {
      slots,
      start: startDate,
      end: endDate,
    });
  }, [step, onSelectSlot]);

  const _teardownSelectable = useCallback(() => {
    if (!selectorRef.current) return;
    selectorRef.current.teardown();
    selectorRef.current = null;
  }, []);

  const _selectable = useCallback(() => {
    const node = nodeRef.current;
    selectorRef.current = new Selection(() => node);

    const selectionState = ({ y }) => {
      const { top, bottom } = getBoundsForNode(node);

      const mins = totalMinRef.current;
      const range = Math.abs(top - bottom);

      let current = (y - top) / range;
      current = snapToSlot(minToDate(mins * current, min), step);

      if (!selecting) {
        initialDateSlotRef.current = current;
      }

      const initial = initialDateSlotRef.current;

      if (dates.eq(initial, current, 'minutes')) {
        current = dates.add(current, step, 'minutes');
      }

      const start = dates.max(min, dates.min(initial, current));
      const end = dates.min(max, dates.max(initial, current));

      return {
        selecting: true,
        startDate: start,
        endDate: end,
        startSlot: positionFromDate(start, min, step),
        endSlot: positionFromDate(end, min, step),
      };
    };

    const maybeSelect = (box) => {
      const state = selectionState(box);
      const { startDate: start, endDate: end } = state;

      if (onSelecting) {
        if (
          (dates.eq(startDate, start, 'minutes') &&
          dates.eq(endDate, end, 'minutes')) ||
          onSelecting({ start, end }) === false
        ) {
          return;
        }
      }

      setSelecting(true);
      setStartDate(start);
      setEndDate(end);
      setStartSlot(positionFromDate(start, min, step));
      setEndSlot(positionFromDate(end, min, step));
    };

    selectorRef.current.on('selecting', maybeSelect);
    selectorRef.current.on('selectStart', maybeSelect);

    selectorRef.current.on('click', ({ x, y }) => {
      clickTimerRef.current = setTimeout(() => {
        _selectSlot(selectionState({ x, y }));
      });

      setSelecting(false);
    });

    selectorRef.current.on('select', () => {
      if (selecting) {
        _selectSlot({ startDate, endDate });
        setSelecting(false);
      }
    });
  }, [min, max, step, onSelecting, selecting, startDate, endDate, _selectSlot]);

  useEffect(() => {
    nodeRef.current = document.querySelector('.rbc-day-slot');
  }, []);

  useEffect(() => {
    if (selectable) {
      _selectable();
    } else {
      _teardownSelectable();
    }

    return () => {
      _teardownSelectable();
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
      }
    };
  }, [selectable, _selectable, _teardownSelectable]);

  const renderEvents = useCallback(() => {
    let lastLeftOffset = 0;
    const maxDate = moment(min).clone().add(1, 'day').toDate();

    const sortedEvents = [...events].sort((a, b) => +get(a, startAccessor) - +get(b, startAccessor));

    return sortedEvents.map((event, idx) => {
      const start = get(event, startAccessor);
      const end = get(event, endAccessor);

      let startSlot = positionFromDate(start, min, step);
      let endSlot = positionFromDate(end, min, step);

      if (start < min) startSlot = 0;
      if (end >= maxDate) endSlot = 1440;
      if (moment(end).isSame(min)) return null;

      lastLeftOffset = Math.max(0,
        overlaps(event, sortedEvents.slice(0, idx), { startAccessor, endAccessor }, lastLeftOffset + 1));

      const style = _slotStyle(startSlot, endSlot, lastLeftOffset);

      const title = get(event, titleAccessor);
      const label = localizer.format({ start, end }, eventTimeRangeFormat, culture);
      const _isSelected = isSelected(event, selected);

      let xStyle;
      let eventClassName;
      if (eventPropGetter) {
        const getter = eventPropGetter(event, start, end, _isSelected);
        xStyle = getter.style;
        eventClassName = getter.className;
      }

      return (
        <Event
          event={event}
          EventComponent={eventComponent}
          key={`evt_${idx}`}
          style={{ ...xStyle, ...style }}
          title={title}
          label={label}
          handleClick={() => _select(event)}
          onClick={() => handleEventClick(event)}
          className={eventClassName}
          isSelected={_isSelected}
          lastLeftOffset={lastLeftOffset}
        />
      );
    });
  }, [events, min, step, culture, eventPropGetter, selected, eventTimeRangeFormat, eventComponent, startAccessor, endAccessor, titleAccessor, _slotStyle, _select]);

  const selectDates = {
    start: startDate,
    end: endDate,
  };

  const style = _slotStyle(startSlot, endSlot, 0);

  return (
    <TimeColumn
      min={min}
      max={max}
      step={step}
      timeslots={timeslots}
      now={now}
      className="rbc-day-slot"
      rtl={rtl}
      culture={culture}
    >
      {renderEvents()}
      {selecting && (
        <div className="rbc-slot-selection" style={style}>
          <span>
            {localizer.format(selectDates, selectRangeFormat, culture)}
          </span>
        </div>
      )}
    </TimeColumn>
  );
};

DaySlot.propTypes = {
  events: PropTypes.array.isRequired,
  step: PropTypes.number.isRequired,
  min: PropTypes.instanceOf(Date).isRequired,
  max: PropTypes.instanceOf(Date).isRequired,

  allDayAccessor: accessor.isRequired,
  startAccessor: accessor.isRequired,
  endAccessor: accessor.isRequired,

  selectable: PropTypes.bool,
  eventOffset: PropTypes.number,

  onSelecting: PropTypes.func,
  onSelectSlot: PropTypes.func.isRequired,
  onSelectEvent: PropTypes.func.isRequired,

  className: PropTypes.string,
  rtl: PropTypes.bool,

  now: PropTypes.instanceOf(Date),
  selectRangeFormat: PropTypes.func,
  culture: PropTypes.string,
  timeslots: PropTypes.any,
  selected: PropTypes.object,
  titleAccessor: PropTypes.string,
  eventTimeRangeFormat: PropTypes.func,
  eventPropGetter: PropTypes.func,
  eventComponent: PropTypes.func,
};

export default DaySlot;